import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import MultiSelectFilter from './MultiSelectFilter';
import api from '../lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','7XL','8XL','9XL','Free Size','32','34','36','38','40','42'];
const sortSizes = (sizes) => [...sizes].sort((a, b) => {
  const ai = SIZE_ORDER.indexOf(a);
  const bi = SIZE_ORDER.indexOf(b);
  return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
});

const DOWNLOAD_FIELDS = [
  { key: 'colors', label: 'Colours' },
  { key: 'sizes', label: 'Sizes' },
  { key: 'qty', label: 'Qty per Size (Color-wise)' },
  { key: 'wsp', label: 'WSP' },
];

const Catalogue = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [generating, setGenerating] = useState(false);

  // Data sources
  const [brands, setBrands] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [enums, setEnums] = useState({ categories: [], design_themes: [], sizes: [], colors: [], fabrics: [] });

  // Filters
  const [filterBrands, setFilterBrands] = useState([]);
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterVendors, setFilterVendors] = useState([]);
  const [filterVendorCities, setFilterVendorCities] = useState([]);
  const [filterOccasions, setFilterOccasions] = useState([]);
  const [filterFabrics, setFilterFabrics] = useState([]);
  const [filterColors, setFilterColors] = useState([]);
  const [filterSizes, setFilterSizes] = useState([]);
  const [filterStock, setFilterStock] = useState('all'); // all | in | out | low
  const [lowStockThreshold, setLowStockThreshold] = useState(10);

  // Cache of full unfiltered catalogue for narrowing filter options to real values
  const [allProducts, setAllProducts] = useState([]);

  // Download options
  const [includeFields, setIncludeFields] = useState(['sizes', 'wsp']);
  const [showDownloadOpts, setShowDownloadOpts] = useState(false);

  // WhatsApp share modal
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [waProducts, setWaProducts] = useState([]);
  const [waSelectedImages, setWaSelectedImages] = useState({});
  const [waGenerating, setWaGenerating] = useState(false);

  useEffect(() => {
    api.get('/brands').then(r => setBrands(r.data || [])).catch(() => {});
    api.get('/vendors').then(r => setVendors(r.data || [])).catch(() => {});
    api.get('/enums').then(r => setEnums(r.data || {})).catch(() => {});
    // Cache the full unfiltered universe to derive real filter options
    api.get('/catalogue/products').then(r => setAllProducts(r.data || [])).catch(() => {});
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filterBrands.length) p.append('brand_id', filterBrands.join(','));
      if (filterCategories.length) p.append('category', filterCategories.join(','));
      if (filterOccasions.length) p.append('occasion', filterOccasions.join(','));
      if (filterFabrics.length) p.append('fabric', filterFabrics.join(','));
      if (filterColors.length) p.append('color', filterColors.join(','));
      if (filterSizes.length) p.append('size', filterSizes.join(','));
      const vendorIds = new Set(filterVendors);
      if (filterVendorCities.length) {
        vendors.filter(v => filterVendorCities.includes(v.city)).forEach(v => vendorIds.add(v.id));
      }
      if (vendorIds.size > 0) p.append('vendor_id', [...vendorIds].join(','));
      if (search) p.append('search', search);
      const res = await api.get(`/catalogue/products?${p.toString()}`);
      setProducts(res.data || []);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  }, [filterBrands, filterCategories, filterVendors, filterVendorCities, filterOccasions, filterFabrics, filterColors, filterSizes, vendors, search]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === visibleProducts.length) setSelected(new Set());
    else setSelected(new Set(visibleProducts.map(p => p.id)));
  };

  const toggleField = (key) => {
    setIncludeFields(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const generatePDF = async () => {
    if (selected.size === 0) { toast.error('Select at least one product'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/catalogue/generate-pdf', { product_ids: [...selected], include_fields: includeFields }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'catalogue.pdf'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF catalogue downloaded!');
    } catch { toast.error('Failed to generate PDF'); }
    finally { setGenerating(false); }
  };

  const downloadImages = async () => {
    if (selected.size === 0) { toast.error('Select at least one product'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/catalogue/download-images', { product_ids: [...selected], include_fields: includeFields }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a'); a.href = url; a.download = 'catalogue_images.zip'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Images downloaded as ZIP!');
    } catch { toast.error('Failed to download images'); }
    finally { setGenerating(false); }
  };

  // WhatsApp share flow
  const openWhatsAppModal = async () => {
    if (selected.size === 0) { toast.error('Select at least one product'); return; }
    try {
      const res = await api.post('/catalogue/product-images', { product_ids: [...selected] });
      const prods = res.data || [];
      setWaProducts(prods);
      // Default: select first image for each product
      const defaults = {};
      prods.forEach(p => { if (p.images.length > 0) defaults[p.id] = [0]; });
      setWaSelectedImages(defaults);
      setShowWhatsApp(true);
    } catch { toast.error('Failed to load product images'); }
  };

  const toggleWaImage = (productId, imgIdx) => {
    setWaSelectedImages(prev => {
      const current = prev[productId] || [];
      const next = current.includes(imgIdx) ? current.filter(i => i !== imgIdx) : [...current, imgIdx].sort();
      return { ...prev, [productId]: next };
    });
  };

  const selectAllImagesForProduct = (productId, totalImages) => {
    setWaSelectedImages(prev => {
      const current = prev[productId] || [];
      const allSelected = current.length === totalImages;
      return { ...prev, [productId]: allSelected ? [0] : Array.from({ length: totalImages }, (_, i) => i) };
    });
  };

  const shareViaWhatsApp = async () => {
    setWaGenerating(true);
    try {
      const productIds = waProducts.filter(p => (waSelectedImages[p.id] || []).length > 0).map(p => p.id);
      if (productIds.length === 0) { toast.error('Select at least one image'); setWaGenerating(false); return; }

      const res = await api.post('/catalogue/whatsapp-image', {
        product_ids: productIds,
        include_fields: includeFields,
        selected_images: waSelectedImages,
      }, { responseType: 'blob' });

      const blob = new Blob([res.data], { type: 'image/jpeg' });
      const designCodes = waProducts.filter(p => productIds.includes(p.id)).map(p => p.design_code);
      const fileName = designCodes.join('_') + '.jpg';

      // Try Web Share API first (works on mobile — attaches image directly)
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], fileName, { type: 'image/jpeg' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: 'Product Catalogue' });
          toast.success('Shared successfully!');
          setShowWhatsApp(false);
          setWaGenerating(false);
          return;
        }
      }

      // Fallback: download image + open WhatsApp with text
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      window.URL.revokeObjectURL(url);

      const text = encodeURIComponent(`Product Catalogue: ${designCodes.join(', ')}\nPlease check the attached image for details.`);
      window.open(`https://wa.me/?text=${text}`, '_blank');
      toast.success('Image downloaded! Attach it in the WhatsApp chat.');
      setShowWhatsApp(false);
    } catch { toast.error('Failed to generate share image'); }
    finally { setWaGenerating(false); }
  };

  // Stock totals helper
  const productStockTotal = useCallback((p) => {
    const csq = p.color_size_qty || {};
    let total = 0;
    Object.values(csq).forEach(sizeMap => {
      Object.values(sizeMap || {}).forEach(q => { total += Number(q) || 0; });
    });
    return total;
  }, []);

  // Client-side stock filter applied on top of server-filtered products
  const visibleProducts = React.useMemo(() => {
    if (filterStock === 'all') return products;
    return products.filter(p => {
      const t = productStockTotal(p);
      if (filterStock === 'in') return t > 0;
      if (filterStock === 'out') return t === 0;
      if (filterStock === 'low') return t > 0 && t < (Number(lowStockThreshold) || 0);
      return true;
    });
  }, [products, filterStock, lowStockThreshold, productStockTotal]);

  // Derive filter options from the full catalogue universe (so users see only real values).
  // Always include currently-selected values to keep them visible even if narrowed out.
  const sortStr = (a, b) => String(a.label).localeCompare(String(b.label));
  const uniqByValue = (arr) => {
    const seen = new Set();
    return arr.filter(o => { if (seen.has(o.value)) return false; seen.add(o.value); return true; });
  };

  const universeSizes = new Set();
  const universeColors = new Set();
  const universeBrands = new Set();
  const universeCategories = new Set();
  const universeOccasions = new Set();
  const universeFabrics = new Set();
  const universeVendors = new Set();
  const universeCities = new Set();
  (allProducts || []).forEach(p => {
    (p.sizes || []).forEach(s => universeSizes.add(s));
    Object.keys(p.color_size_qty || {}).forEach(c => universeColors.add(c));
    if (p.brand_id) universeBrands.add(p.brand_id);
    if (p.category) universeCategories.add(p.category);
    if (p.occasion) universeOccasions.add(p.occasion);
    if (p.fabric) universeFabrics.add(p.fabric);
    if (p.vendor_id) universeVendors.add(p.vendor_id);
  });
  vendors.forEach(v => { if (v.city && universeVendors.has(v.id)) universeCities.add(v.city); });

  const brandOpts = uniqByValue([
    ...brands.filter(b => universeBrands.has(b.id)).map(b => ({ value: b.id, label: b.name })),
    ...brands.filter(b => filterBrands.includes(b.id)).map(b => ({ value: b.id, label: b.name })),
  ]).sort(sortStr);
  const catOpts = uniqByValue([
    ...[...universeCategories].map(c => ({ value: c, label: c })),
    ...filterCategories.map(c => ({ value: c, label: c })),
  ]).sort(sortStr);
  const occasionOpts = uniqByValue([
    ...[...universeOccasions].map(t => ({ value: t, label: t })),
    ...filterOccasions.map(t => ({ value: t, label: t })),
  ]).sort(sortStr);
  const fabricOpts = uniqByValue([
    ...[...universeFabrics].map(f => ({ value: f, label: f })),
    ...filterFabrics.map(f => ({ value: f, label: f })),
  ]).sort(sortStr);
  const colorOpts = uniqByValue([
    ...[...universeColors].map(c => ({ value: c, label: c })),
    ...filterColors.map(c => ({ value: c, label: c })),
  ]).sort(sortStr);
  const sizeOpts = uniqByValue([
    ...sortSizes([...universeSizes]).map(s => ({ value: s, label: s })),
    ...filterSizes.map(s => ({ value: s, label: s })),
  ]);
  const vendorOpts = uniqByValue([
    ...vendors.filter(v => universeVendors.has(v.id)).map(v => ({ value: v.id, label: v.name + (v.city ? ` (${v.city})` : '') })),
    ...vendors.filter(v => filterVendors.includes(v.id)).map(v => ({ value: v.id, label: v.name + (v.city ? ` (${v.city})` : '') })),
  ]).sort(sortStr);
  const vendorCityOpts = uniqByValue([
    ...[...universeCities].map(c => ({ value: c, label: c })),
    ...filterVendorCities.map(c => ({ value: c, label: c })),
  ]).sort(sortStr);
  const hasFilter = filterBrands.length || filterCategories.length || filterVendors.length || filterVendorCities.length || filterOccasions.length || filterFabrics.length || filterColors.length || filterSizes.length || filterStock !== 'all';

  const clearAllFilters = () => {
    setFilterBrands([]); setFilterCategories([]); setFilterVendors([]); setFilterVendorCities([]);
    setFilterOccasions([]); setFilterFabrics([]); setFilterColors([]); setFilterSizes([]);
    setFilterStock('all');
  };

  return (
    <div className="space-y-4 p-6" data-testid="catalogue-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }} data-testid="catalogue-title">Catalogue</h1>
          <p className="text-sm text-gray-500">Select products and generate shareable catalogues</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Button variant="outline" size="sm" className="h-9 text-xs border-gray-300" onClick={() => setShowDownloadOpts(!showDownloadOpts)} data-testid="download-options-btn">
              Download Options ({includeFields.length})
              <svg className={`w-3.5 h-3.5 ml-1 transition-transform ${showDownloadOpts ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
            </Button>
            {showDownloadOpts && (
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-xl z-50 w-56 p-2" data-testid="download-options-panel">
                <p className="text-xs font-semibold text-gray-500 px-2 pb-1 border-b mb-1">Include in downloads:</p>
                {DOWNLOAD_FIELDS.map(f => (
                  <label key={f.key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer text-sm" data-testid={`download-opt-${f.key}`}>
                    <Checkbox checked={includeFields.includes(f.key)} onCheckedChange={() => toggleField(f.key)} />
                    <span>{f.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <Button onClick={generatePDF} disabled={selected.size === 0 || generating} className="bg-red-600 hover:bg-red-700" data-testid="generate-pdf-btn">
            {generating ? 'Generating...' : `PDF (${selected.size})`}
          </Button>
          <Button onClick={downloadImages} disabled={selected.size === 0 || generating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-images-btn">
            Images ZIP ({selected.size})
          </Button>
          <Button onClick={openWhatsAppModal} disabled={selected.size === 0 || generating} className="bg-green-600 hover:bg-green-700" data-testid="whatsapp-share-btn">
            <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp ({selected.size})
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="catalogue-filters"><CardContent className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[140px]">
            <Label className="text-xs mb-1 block">Vendor</Label>
            <MultiSelectFilter label="All Vendors" options={vendorOpts} selected={filterVendors} onChange={setFilterVendors} testId="catalogue-vendor-filter" />
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs mb-1 block">Vendor Area</Label>
            <MultiSelectFilter label="All Areas" options={vendorCityOpts} selected={filterVendorCities} onChange={setFilterVendorCities} testId="catalogue-vendor-city-filter" />
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs mb-1 block">Brand</Label>
            <MultiSelectFilter label="All Brands" options={brandOpts} selected={filterBrands} onChange={setFilterBrands} testId="catalogue-brand-filter" />
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs mb-1 block">Category</Label>
            <MultiSelectFilter label="All Categories" options={catOpts} selected={filterCategories} onChange={setFilterCategories} testId="catalogue-category-filter" />
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs mb-1 block">Occasion</Label>
            <MultiSelectFilter label="All Occasions" options={occasionOpts} selected={filterOccasions} onChange={setFilterOccasions} testId="catalogue-occasion-filter" />
          </div>
          <div className="min-w-[120px]">
            <Label className="text-xs mb-1 block">Fabric</Label>
            <MultiSelectFilter label="All Fabrics" options={fabricOpts} selected={filterFabrics} onChange={setFilterFabrics} testId="catalogue-fabric-filter" />
          </div>
          <div className="min-w-[120px]">
            <Label className="text-xs mb-1 block">Color</Label>
            <MultiSelectFilter label="All Colors" options={colorOpts} selected={filterColors} onChange={setFilterColors} testId="catalogue-color-filter" />
          </div>
          <div className="min-w-[110px]">
            <Label className="text-xs mb-1 block">Size</Label>
            <MultiSelectFilter label="All Sizes" options={sizeOpts} selected={filterSizes} onChange={setFilterSizes} testId="catalogue-size-filter" />
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs mb-1 block">Stock</Label>
            <select
              value={filterStock}
              onChange={e => setFilterStock(e.target.value)}
              className="w-full h-8 px-2 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              data-testid="catalogue-stock-filter"
            >
              <option value="all">All</option>
              <option value="in">In Stock</option>
              <option value="out">Out of Stock</option>
              <option value="low">Low Stock</option>
            </select>
          </div>
          {filterStock === 'low' && (
            <div className="min-w-[90px]">
              <Label className="text-xs mb-1 block">Low &lt;</Label>
              <Input
                type="number"
                min="1"
                value={lowStockThreshold}
                onChange={e => setLowStockThreshold(e.target.value)}
                className="h-8 text-xs"
                data-testid="catalogue-low-stock-threshold"
              />
            </div>
          )}
          <div className="min-w-[180px]">
            <Label className="text-xs mb-1 block">Search</Label>
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search design code..." className="h-8 text-xs" data-testid="catalogue-search" />
          </div>
          {hasFilter && <Button variant="ghost" size="sm" className="text-red-500 h-8" onClick={clearAllFilters}>Clear</Button>}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={selectAll} data-testid="select-all-btn">
              {selected.size === visibleProducts.length && visibleProducts.length > 0 ? 'Deselect All' : 'Select All'}
            </Button>
            <span className="text-xs text-gray-400" data-testid="catalogue-product-count">
              {filterStock === 'all' ? `${visibleProducts.length} products` : `${visibleProducts.length} of ${products.length} products`}
            </span>
          </div>
        </div>
      </CardContent></Card>

      {/* Product Grid */}
      {loading ? (
        <Card><CardContent className="p-12 text-center text-gray-400">Loading products...</CardContent></Card>
      ) : visibleProducts.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-400" data-testid="catalogue-empty">No products found. {hasFilter ? 'Try clearing filters.' : 'Add products to the catalogue first.'}</CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="catalogue-grid">
          {visibleProducts.map(p => {
            const csq = p.color_size_qty || {};
            const sizes = sortSizes(p.sizes || []);
            return (
              <div
                key={p.id}
                onClick={() => toggleSelect(p.id)}
                className={`cursor-pointer border rounded-xl overflow-hidden transition-all ${selected.has(p.id) ? 'ring-2 ring-emerald-500 border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm bg-white'}`}
                data-testid={`product-card-${p.id}`}
              >
                <div className="flex items-center justify-between px-2.5 pt-2.5">
                  <Checkbox checked={selected.has(p.id)} className="pointer-events-none" />
                  {p.brand && <Badge className="bg-indigo-100 text-indigo-700 text-[9px]">{p.brand}</Badge>}
                </div>
                <div className="aspect-square mx-2.5 mt-1.5 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                  {p.image ? (
                    <img src={`${API_BASE}${p.image}`} alt={p.design_code} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <span className="text-gray-300 text-xs">No Image</span>
                  )}
                </div>
                <div className="px-2.5 py-2">
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-sm text-gray-900 truncate">{p.design_code}</p>
                    <p className="text-emerald-700 font-bold text-xs">WSP: {p.wsp}</p>
                  </div>
                  {sizes.length > 0 && (
                    <div className="flex flex-wrap gap-0.5 mt-1">
                      {sizes.map(s => (
                        <span key={s} className="text-[9px] bg-blue-50 text-blue-700 px-1 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                  )}
                  {Object.keys(csq).length > 0 && (
                    <div className="mt-1.5 space-y-0.5">
                      {Object.keys(csq).sort().map(color => (
                        <div key={color} className="text-[9px] leading-tight">
                          <span className="font-semibold text-purple-700">{color}</span>
                          <span className="text-gray-500 ml-1">
                            {sizes.map(s => `${s}:${csq[color]?.[s] ?? 0}`).join(', ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* WhatsApp Share Modal */}
      {showWhatsApp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowWhatsApp(false)} data-testid="whatsapp-modal-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="whatsapp-modal">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-green-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Share via WhatsApp</h3>
                  <p className="text-xs text-gray-500">Select images to include in the share</p>
                </div>
              </div>
              <button onClick={() => setShowWhatsApp(false)} className="text-gray-400 hover:text-gray-600" data-testid="whatsapp-modal-close">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Modal Body — Image Selector */}
            <div className="overflow-y-auto px-6 py-4" style={{ maxHeight: 'calc(80vh - 160px)' }}>
              {waProducts.map(p => (
                <div key={p.id} className="mb-4 last:mb-0" data-testid={`wa-product-${p.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-bold text-sm text-gray-800">{p.design_code}</p>
                    {p.images.length > 1 && (
                      <button onClick={() => selectAllImagesForProduct(p.id, p.images.length)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid={`wa-select-all-${p.id}`}>
                        {(waSelectedImages[p.id] || []).length === p.images.length ? 'Select first only' : 'Select all'}
                      </button>
                    )}
                  </div>
                  {p.images.length === 0 ? (
                    <div className="text-xs text-gray-400 italic">No images available</div>
                  ) : (
                    <div className="flex gap-2 flex-wrap">
                      {p.images.map((img, idx) => {
                        const isSelected = (waSelectedImages[p.id] || []).includes(idx);
                        return (
                          <div
                            key={idx}
                            onClick={() => toggleWaImage(p.id, idx)}
                            className={`w-20 h-20 rounded-lg overflow-hidden border-2 cursor-pointer transition-all relative ${isSelected ? 'border-green-500 ring-2 ring-green-300' : 'border-gray-200 opacity-50 hover:opacity-80'}`}
                            data-testid={`wa-img-${p.id}-${idx}`}
                          >
                            <img src={`${API_BASE}${img}`} alt={`${p.design_code} ${idx + 1}`} className="w-full h-full object-cover" />
                            {isSelected && (
                              <div className="absolute top-1 right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {Object.values(waSelectedImages).flat().length} image(s) selected from {waProducts.filter(p => (waSelectedImages[p.id] || []).length > 0).length} product(s)
              </p>
              <Button onClick={shareViaWhatsApp} disabled={waGenerating || Object.values(waSelectedImages).flat().length === 0}
                className="bg-green-600 hover:bg-green-700 text-sm" data-testid="wa-share-btn">
                {waGenerating ? 'Generating...' : `Share via WhatsApp (${waProducts.filter(p => (waSelectedImages[p.id] || []).length > 0).length})`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalogue;
